/**
 * Level 1 — the portfolio. Which projects carry the value, which carry the risk.
 */
import { motion } from 'framer-motion';
import { Building2, ChevronRight, AlertTriangle } from 'lucide-react';
import { Panel, fmtINR, ShareBar, RiskPill, riskHue } from './portfolioUi';

export const LevelPortfolio = ({ projects, onOpenProject, topRisk, onOpenContract }) => {
  const max = Math.max(1, ...projects.map(p => p.value));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="level-portfolio">
      <Panel
        className="lg:col-span-8"
        title="Projects"
        hint="Value concentration and contractual exposure, highest first"
        testId="portfolio-projects-panel"
      >
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {projects.map((p, i) => (
            <motion.button
              key={p.project_id}
              type="button"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: Math.min(i, 10) * 0.03 }}
              whileHover={{ y: -1 }}
              onClick={() => onOpenProject(p)}
              className="w-full text-left py-4 grid grid-cols-[1fr_auto] gap-4 items-center"
              data-testid={`drilldown-row-project-${p.project_id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} />
                  <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {p.project_name}
                  </span>
                  <RiskPill score={p.avg_risk} />
                  {p.red_flags > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#B91C1C' }}>
                      <AlertTriangle className="w-3 h-3" /> {p.red_flags}
                    </span>
                  )}
                </div>
                <div className="mt-2 pr-6"><ShareBar pct={(p.value / max) * 100} hue={riskHue(p.avg_risk)} /></div>
                <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                  {p.vendors.length} vendors · {p.contracts_count} contracts · {p.claims} claims · {p.documents} documents · {p.linked_invoices} invoices linked
                </p>
              </div>
              <div className="text-right shrink-0 flex items-center gap-3">
                <div>
                  <p className="text-lg tabular-nums font-light" style={{ color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace' }}>
                    {fmtINR(p.value)}
                  </p>
                  {p.ld_exposure > 0 && (
                    <p className="text-[11px] tabular-nums" style={{ color: '#B45309' }}>LD {fmtINR(p.ld_exposure)}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              </div>
            </motion.button>
          ))}
          {!projects.length && (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>No contracts in the portfolio yet.</p>
          )}
        </div>
      </Panel>

      <Panel className="lg:col-span-4" title="Riskiest contracts" hint="Cognara review score, worst first" testId="portfolio-top-risk">
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {topRisk.map((c) => (
            <button key={c.id} onClick={() => onOpenContract(c.id)}
                    className="w-full text-left py-3 flex items-center gap-3"
                    data-testid={`top-risk-${c.id}`}>
              <span className="w-1 h-9 rounded-full shrink-0" style={{ background: riskHue(c.risk) }} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm truncate" style={{ color: 'var(--text-primary)' }}>{c.title}</span>
                <span className="block text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>
                  {c.project} · {fmtINR(c.value)}
                </span>
              </span>
              <RiskPill score={c.risk} />
            </button>
          ))}
          {!topRisk.length && (
            <p className="text-sm py-6 text-center" style={{ color: 'var(--text-muted)' }}>
              No contract has been reviewed yet — run a Cognara review to populate this.
            </p>
          )}
        </div>
      </Panel>
    </div>
  );
};

export default LevelPortfolio;
